import { Suspense, type ComponentProps } from 'react';
import {
  CodeBlock,
  FormattedDateTime,
  Heading,
  InlineStack,
  Label,
  Panel,
  PanelSection,
  Paragraph,
  Table,
  TableTbody,
  TableTd,
  TableTh,
  TableThead,
  TableTr,
} from '@customerio/pluma-components/react';
import { AppErrorBoundary } from '@/components/app-error-boundary';
import type { Delivery } from '@/services/db/delivery/entity';
import type { SubjectDetail, SubjectWebhookResponse } from '@/services/db/subject/entity';
import { useSubject } from '@/services/db/subject/queries';
import { isEmptyString } from '@/utils/string';

type WebhookResponseBlockProps = { delivery: Delivery };

/**
 * The webhook response block below a webhook delivery's request body: the last
 * response's status + body, plus a per-attempt table when the send retried
 * (Ember `cio-delivery-detail/preview` webhook branch). A webhook delivery
 * always shows the Response section — it falls back to a "no response data"
 * empty state when the journey subject has no matching step, no attempts, is
 * absent, or fails to load.
 */
export function WebhookResponseBlock({ delivery }: WebhookResponseBlockProps) {
  if (delivery.action_type !== 'webhook_action') {
    return null;
  }
  const subjectId = delivery.subject_id;
  if (subjectId == null || isEmptyString(subjectId)) {
    return <WebhookResponsePanel response={null} />;
  }
  return (
    <AppErrorBoundary fallback={<WebhookResponsePanel response={null} />} resetKeys={[delivery.id]}>
      <Suspense fallback={null}>
        <WebhookResponseContent delivery={delivery} subjectId={subjectId} />
      </Suspense>
    </AppErrorBoundary>
  );
}

type WebhookResponseContentProps = { delivery: Delivery; subjectId: string };

function WebhookResponseContent({ delivery, subjectId }: WebhookResponseContentProps) {
  const { data } = useSubject({ id: subjectId });
  return <WebhookResponsePanel response={webhookResponseOf(data, delivery.id) ?? null} />;
}

type WebhookResponsePanelProps = { response: SubjectWebhookResponse | null };

function WebhookResponsePanel({ response }: WebhookResponsePanelProps) {
  const hasResponse = response != null && (response.attempts?.length ?? 0) > 0;

  return (
    <Panel mt="200" data-test-webhook-response>
      <PanelSection>
        {hasResponse ? <WebhookResponseDetails response={response} /> : <WebhookNoResponse />}
      </PanelSection>
    </Panel>
  );
}

function WebhookNoResponse() {
  return (
    <>
      <Heading level="3">Response</Heading>
      <Paragraph color="subtle" data-test-webhook-no-response>
        No response data available.
      </Paragraph>
    </>
  );
}

type WebhookResponseDetailsProps = { response: SubjectWebhookResponse };

function WebhookResponseDetails({ response }: WebhookResponseDetailsProps) {
  const body = responseBody(response.last_response);
  const statusCode = lastStatusCode(response);

  return (
    <>
      <InlineStack gap="100" alignBlock="center">
        <Heading level="3" data-test-webhook-response-header>
          Response
        </Heading>
        {statusCode != null ? <HttpStatusLabel status={statusCode} /> : null}
      </InlineStack>

      {body != null ? (
        <CodeBlock
          language={responseLanguage(response.last_response)}
          code={body}
          withLineNumbers
          className="fs-mask"
          data-test-webhook-response-body
        />
      ) : (
        <Paragraph color="subtle" data-test-webhook-response-empty>
          No response body to display.
        </Paragraph>
      )}

      {response.attempts.length > 1 ? <AttemptsTable attempts={response.attempts} /> : null}
    </>
  );
}

type AttemptsTableProps = { attempts: SubjectWebhookResponse['attempts'] };

function AttemptsTable({ attempts }: AttemptsTableProps) {
  return (
    <>
      <Heading level="4" mt="200" mb="100">
        Attempts
      </Heading>
      <Table caption="Webhook response attempts" data-test-webhook-response-attempts>
        <TableThead>
          <TableTr>
            <TableTh>Status</TableTh>
            <TableTh>Timestamp</TableTh>
          </TableTr>
        </TableThead>
        <TableTbody>
          {attempts.map((attempt, index) => (
            <TableTr key={index} data-test-webhook-attempt>
              <TableTd>{attempt.status_code !== 0 ? <HttpStatusLabel status={attempt.status_code} /> : null}</TableTd>
              <TableTd>
                {attempt.timestamp !== 0 ? (
                  <FormattedDateTime date={attempt.timestamp} format="medium_with_time" />
                ) : null}
              </TableTd>
            </TableTr>
          ))}
        </TableTbody>
      </Table>
    </>
  );
}

type HttpStatusLabelProps = { status: number };

/** HTTP status pill, colored by class: 1xx blue, 2xx green, 3xx yellow, else red (Ember `http-status-label`). */
function HttpStatusLabel({ status }: HttpStatusLabelProps) {
  return (
    <Label color={httpStatusColor(status)} data-test-webhook-response-status>
      {status}
    </Label>
  );
}

type LabelColor = ComponentProps<typeof Label>['color'];

function httpStatusColor(status: number): LabelColor {
  const s = String(status);
  if (/^1\d\d/.test(s)) {
    return 'blue';
  }
  if (/^2\d\d/.test(s)) {
    return 'green';
  }
  if (/^3\d\d/.test(s)) {
    return 'yellow';
  }
  return 'red';
}

/** The webhook response for this delivery, from the subject step whose `delivery_id` matches. */
function webhookResponseOf({ subject_steps }: SubjectDetail, deliveryId: string): SubjectWebhookResponse | undefined {
  return subject_steps.find((s) => s.delivery_id === deliveryId)?.webhook_response;
}

/** The last attempt's status code, or `undefined` when it is absent/zero (Ember guards `0` as falsy). */
function lastStatusCode(response: SubjectWebhookResponse): number | undefined {
  const last = response.attempts.at(-1);
  if (last == null || last.status_code === 0) {
    return undefined;
  }
  return last.status_code;
}

/**
 * Pretty-printed response body (JSON when parseable), or `undefined` when empty.
 * Only a truly empty (or absent) body counts as empty — Ember does not trim, so a
 * whitespace-only response still renders. `last_response` is typed required but
 * the API can omit it at runtime for an empty body (same spec/runtime gap the
 * sideloads guard against), so accept null/undefined rather than throwing.
 */
function responseBody(lastResponse: string | null | undefined): string | undefined {
  if (lastResponse == null || lastResponse.length === 0) {
    return undefined;
  }
  try {
    return JSON.stringify(JSON.parse(lastResponse), null, 2);
  } catch {
    return lastResponse;
  }
}

/** JSON bodies get syntax highlighting; anything else renders as plain text. */
function responseLanguage(lastResponse: string | null | undefined): 'json' | 'plain' {
  if (lastResponse == null || lastResponse.length === 0) {
    return 'plain';
  }
  try {
    JSON.parse(lastResponse);
    return 'json';
  } catch {
    return 'plain';
  }
}
